import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { commonService } from './common.service';

/**
 * Holds the `organization_plan` block — tells the app whether this
 * organization is still inside its free trial (recruitment actions don't
 * deduct wallet coins) or the trial has ended (they now do).
 *
 * Seeded at login from the login response (see
 * AuthenticationService.clientLogin) and from localStorage so a page
 * refresh doesn't lose it before the next login. `loadOrgPlan()` refreshes
 * it live from `/wallets/organization-plan/` — the login snapshot alone
 * would otherwise go stale if the trial ends while the user stays logged
 * in, since nothing would re-fetch it until their next login.
 *
 * Not part of the shared services barrel (index.ts) — matches how
 * WalletService / CreditsService are imported directly.
 */
@Injectable({
  providedIn: 'root',
})
export class OrgPlanService {
  private orgPlanSubject = new BehaviorSubject<any>(
    JSON.parse(localStorage.getItem('organization_plan') || 'null'),
  );

  orgPlan$ = this.orgPlanSubject.asObservable();

  constructor(private common: commonService) {}

  setOrgPlan(plan: any) {
    localStorage.setItem('organization_plan', JSON.stringify(plan ?? null));
    this.orgPlanSubject.next(plan ?? null);
  }

  /**
   * Refreshes the plan from the backend so a trial that ends mid-session
   * (not just at the next login) is picked up — e.g. called on header
   * load, the same way WalletService.loadWallet() keeps the coin balance
   * current. Accepts either the plan object directly or one wrapped as
   * `{ organization_plan: {...} }`, since the exact response shape for
   * this endpoint (as opposed to the login response) isn't locked down.
   */
  loadOrgPlan() {
    this.common.get('/wallets/organization-plan/').subscribe({
      next: (res: any) => {
        this.setOrgPlan(res?.organization_plan ?? res ?? null);
      },
      error: (err: any) => {
        console.error('Failed to load organization plan', err);
      },
    });
  }

  getOrgPlan() {
    return this.orgPlanSubject.value;
  }

  /** True while the org is still inside its free-trial window. */
  isFreeTier(): boolean {
    return !!this.orgPlanSubject.value?.free_tier;
  }
}
